import { List, Map, Record } from 'immutable'
import React from 'react'

import { NOTIFICATION_HOURS_DEFAULTS } from 'app/constants/notificationHours'

import { IActivityRecord } from 'store/entities/activity/interface'

import { CHANGE_BUTTON_TYPES } from '@prohabits/metaverse'

export interface ISprintProps {
  userAvatar: React.ReactNode
  organizationAvatar: React.ReactNode
  sprint: any
}

export interface ISprintGoal {
  achievedPoints: number
  goal: number
  isFinished?: boolean
  goalAchievedMessage: boolean | string
}

export interface IAuthToken {
  accessToken: null | string
  expiresIn: null | string
  authToken?: null | string
}

export interface IAuthTokenRecord extends Record<IAuthToken> {}

export interface IIdentityPending {
  getProgress: boolean
  getProfile: boolean
  saveProfile: boolean
  getEmailQuery: boolean
  proTrackSubscription: boolean
  getHistory: boolean
  getCombinedProgress: boolean
}
export interface IIdentityPendingRecord extends Record<IIdentityPending> {}

export interface IIdentityBeforeLoading {
  profile: boolean
}

export interface IProgressAllInstance {
  commitsCount: number
  completedCount: number
  streakCurrent: number
  streakMax: number
  storiesShared: number
  storiesReactions: number
}

export interface IProgressCurrentInstance {
  commitsCount: number
  completedCount: number
  progressDayForLevel: number
  proTrackLevelLength: number
  proTrackProgress: number
  streakCurrentForLevel: number
  streakMaxForLevel: number
  storiesShared: number
  storiesReactions: number
}

export interface IProgress {
  all: IProgressAllInstance
  current: IProgressCurrentInstance
}

export interface IProgresRecord
  extends Record<{
    all: Record<IProgressAllInstance>
    current: Record<IProgressCurrentInstance>
  }> {}

export interface IOnboardingProTrackRecord {
  nextLevel: any
  previousLevels: List<any>
}

export interface IOnboardingLevels {
  current: Map<any, any> | any
  new: List<any>
  previous: List<any>
  levels: List<any>
}

export interface IOnboardingLevelsRecord extends Record<IOnboardingLevels> {}

export interface IOnboardingLevel {
  color: string | null
  exampleActivities: List<any>
  finishText: string | null
  goals: string | null
  id: number | null
  image: string | null
  name: string | null
  order: number | null
  postSurveyLink: string | null
  preSurveyLink: string | null
  proTrackId: number | null
  proTrackName: string | null
  welcomeText: string | null
  activities: List<IActivityRecord>
  finished?: boolean
}

export interface IIdentityOrganization {
  active: boolean
  domain: string | null
  id: number | null
  logo: string | null
  name: string | null
  hasIrregularWorkWeek: boolean
  demoMode: boolean
  teamOnly: boolean
  proTrackLevelEndsByActivityCount: boolean
  isApiKeyEnabled: boolean
  emailProviderApiKey: string | null
  emailFrom: string | null
  displayName: string | null
  sms: boolean
  stories: boolean
  reactions: boolean
  peerInvites: boolean
  social: boolean
  welcomeEmail: Map<any, any>
  language: string | null
  isSprintsActive: boolean
  changeNotificationFrequency: boolean
  isPreTrackSurveyEnabled: boolean
  isPostTrackSurveyEnabled: boolean
}
export interface IIdentityOrganizationRecord extends Record<IIdentityOrganization> {}

export interface IActivitiesHistoryPagination {
  limit: null | any
  count: null | any
  page: null | any
}

export interface IActivitiesHistory {
  items: List<IActivitiesHistoryItem>
  pending: boolean
  pagination: Record<IActivitiesHistoryPagination>
}

export interface IActivitiesHistoryRecord extends Record<IActivitiesHistory> {}

export interface IActivitiesHistoryItem {
  activityId: number | null
  image: null | string
  activityVideoId: null | string
  videoThumbnail: null | string
  name: string | null
  description: string | null
  proQuote: string | null
  proQuoteAuthor: string | null
  day: number | null
  completed: boolean | null
  actionType: string | null
  userActivityId: number | null
  userProTrackLevelId: number | null
  hasStory: boolean | null
  question: string | null
  story: {
    id: number | null
    story: string | null
  }
}

export interface INotificationType {
  email: boolean
  sms: boolean
}
export interface INotificationTypeRecord extends Record<INotificationType> {}

export interface IFeaturesNotification {
  sms: boolean | null
  smsTooltip: any
  stories: boolean | null
}

export interface IOnboardingChecklist {
  isFirstActionCompleted: boolean
  isStoryShared: boolean
  isMobileNumberAdded: boolean
  areNotificationSettingsUpdated: boolean
  isQuizCompleted: boolean
}

export interface IOnboardingChecklistRecord extends Record<IOnboardingChecklist> {}

export interface IFeaturesNotificationRecord extends Record<IFeaturesNotification> {}

export interface ISprintGoalRecord extends Record<ISprintGoal> {}

export interface ISprint {
  title: string | null
  name: string | null
  endDate: Date
  badge: null | any
  user: ISprintGoal
  organization: ISprintGoal
  result: any
}
export interface ISprintRecord extends Record<ISprint> {}

export interface IIdentity {
  id: number | null // must be number by default
  role: List<any>
  authToken: IAuthTokenRecord
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: any
  phoneISO: string | number | null
  avatar: string | null
  firstLogin: boolean
  day: any // must be number
  stage: string | null
  roles: List<any>
  activeRole: number
  pending: IIdentityPendingRecord
  activitiesHistory: IActivitiesHistoryRecord
  teams: List<any>
  commitNotificationTime: NOTIFICATION_HOURS_DEFAULTS.MORNING
  committedNotificationTime: NOTIFICATION_HOURS_DEFAULTS.EVENING
  notificationType: INotificationTypeRecord
  timezone: string
  workWeek: any // must be array ['mon','tue']
  isWorkWeekTouched: boolean | null
  actions: Map<any, any>
  activeLevel: any //  TODO Add type for { ActiveProTrackLevel } from 'store/records/proTracks'
  progress: IProgress
  beforeLoading: IIdentityBeforeLoading
  proTrackStartDate: any
  canCompletePreviousActivity: boolean
  dayoffs: List<any>
  organizationId: number | null
  organization: IIdentityOrganizationRecord
  lastActivity: IActivityRecord
  isTokenSendToEmail: boolean
  isTokenSendToPhone: boolean
  onboardingLevels: IOnboardingLevelsRecord
  isShowFeatureInfo: IFeaturesNotificationRecord
  isUserHaveOnlyFirstActivity: boolean
  previousProTrackLevel: any //  TODO Add type for { ActiveProTrackLevel } from 'store/records/proTracks'
  isLoginFlowFinished: boolean
  sprint: ISprintRecord
  badges: any
  oneSignalAppId: any
  oneSignals: List<any>
  isCommitNotificationTimeHasCome: boolean
  isCommittedNotificationTimeHasCome: boolean
  isUserCanSwitchProtrack: boolean
  isWorkingDay: boolean
  onboardingChecklist: IOnboardingChecklistRecord
  isUserEverTakeProTrack: boolean // true if user has been subscribed to at least 1 protrack
}

export interface IIdentityRecord extends Record<IIdentity> {
  getName: () => string
  getInitials: () => string
  getTimezone: () => string
}

export enum EEmailTypes {
  MORNING = 'morning',
  EVENING = 'evening',
  AUTOENROLL = 'autoenroll',
}

export interface IUserActivityClickPayload {
  userActivityId: number
  changeButtonType: CHANGE_BUTTON_TYPES
  source: EEmailTypes
  enrolledDays: number
}

// TODO: @Serginyo90 INVITE is not recorded to db because must change ENUM in database
export enum EStoppedFrom {
  MORNING = 'morning email',
  EVENING = 'evening email',
  AUTOENROLL = 'autoenroll email',
  DASHBOARD = 'dashboard settings',
  ADMIN_PANEL = 'admin page',
  CHANGE_FREQUENCY = 'change frequency email',
  INVITE = 'switch proTrack by going back to invite email',
}

export interface IUpdateOnboardingChecklist {
  propertyName: string
  propertyValue: boolean
}

export interface ISetCustomStory {
  activityId: number
  userActivityId: number
  story: string
  mentions: number[]
}
